import { Select } from '../../components/ui';
import type { SelectOption } from '../../components/ui';

/**
 * Picking an Outbound company or lead from inside Deals.
 *
 * Deals and Outbound are separate modules with separate permissions, so this
 * control has to survive the case where somebody can open Deals but cannot
 * read the Outbound records it wants to offer. That is not an error state:
 * the deal is fine, the link is simply not something this person can set. It
 * renders disabled with a plain explanation rather than an empty dropdown
 * that looks broken, or a query failure that looks like an outage.
 *
 * An existing link still shows its name when we have one, so losing Outbound
 * access never makes an already-linked deal look unlinked.
 */

export const NO_OUTBOUND_HINT = 'Requires Outbound module permission';

export interface PickerEntity {
  id: string;
  label: string;
}

export function outboundOptions(
  entities: PickerEntity[],
  selectedId: string | null,
  emptyLabel: string,
): SelectOption[] {
  const options: SelectOption[] = [
    { value: '', label: emptyLabel },
    ...entities.map((entity) => ({ value: entity.id, label: entity.label })),
  ];
  // A link to something outside the loaded set (archived, or filtered away)
  // would otherwise silently reset the field to "none" the moment the select
  // renders, quietly unlinking a deal nobody meant to touch.
  if (selectedId !== null && !entities.some((entity) => entity.id === selectedId)) {
    options.push({ value: selectedId, label: 'Linked record (not loaded)' });
  }
  return options;
}

export function OutboundPicker({
  label,
  emptyLabel,
  entities,
  selectedId,
  canReachOutbound,
  disabled = false,
  onChange,
}: {
  label: string;
  /** What the "nothing linked" row says, e.g. 'No company'. */
  emptyLabel: string;
  entities: PickerEntity[];
  selectedId: string | null;
  /** False when the permissions matrix denies this person Outbound. */
  canReachOutbound: boolean;
  disabled?: boolean;
  onChange: (nextId: string | null) => void;
}) {
  if (!canReachOutbound) {
    const linked = entities.find((entity) => entity.id === selectedId);
    return (
      <div className="outbound-picker-locked">
        <Select
          label={label}
          labelHidden
          disabled
          value={selectedId ?? ''}
          options={
            selectedId === null
              ? [{ value: '', label: emptyLabel }]
              : [{ value: selectedId, label: linked?.label ?? 'Linked record' }]
          }
          onChange={() => undefined}
        />
        <p className="outbound-picker-hint">{NO_OUTBOUND_HINT}</p>
      </div>
    );
  }

  return (
    <Select
      label={label}
      labelHidden
      disabled={disabled}
      value={selectedId ?? ''}
      options={outboundOptions(entities, selectedId, emptyLabel)}
      onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
    />
  );
}
